import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { Send, Loader2, X, Lock, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Message {
  role: "user" | "assistant";
  content: string;
}

const PLACEHOLDER = "__TO_BE_SPECIFIED__";

interface Syllabus {
  title: string;
  learnerProfile?: string | null;
  timeline?: string | null;
  learningObjectives?: string[] | null;
  successCriteria?: string | null;
  sections: {
    title: string;
    description: string;
    topics: string[];
    duration: string;
    references: { title: string; url: string }[];
  }[];
}

interface CourseDesignChatProps {
  initialTopic: string;
  onClose: () => void;
}

function isPlaceholder(value: string | string[] | null | undefined): boolean {
  if (value == null || value === "") return true;
  if (Array.isArray(value)) {
    return value.length === 0 || value.every((v) => String(v) === PLACEHOLDER || !v);
  }
  return String(value) === PLACEHOLDER;
}

export function CourseDesignChat({ initialTopic, onClose }: CourseDesignChatProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingSyllabus, setIsGeneratingSyllabus] = useState(false);
  const [syllabus, setSyllabus] = useState<Syllabus | null>(null);
  const [email, setEmail] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const syllabusScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialTopic && messages.length === 0) {
      startConversation();
    }
  }, [initialTopic]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const generateInitialSyllabus = async (msgs: Message[]) => {
    setIsGeneratingSyllabus(true);
    toast.info("Generating your syllabus...");
    try {
      const response = await supabase.functions.invoke("course-design-chat", {
        body: {
          messages: msgs,
          generateSyllabus: true,
          generateSyllabusPhase: "initial",
        },
      });
      if (response.error) throw new Error(response.error.message);
      if (response.data.syllabus) {
        setSyllabus(response.data.syllabus);
        toast.success("Syllabus generated!");
      }
    } catch (error) {
      console.error("Error generating syllabus:", error);
      toast.error("Failed to generate syllabus. Please try again.");
    } finally {
      setIsGeneratingSyllabus(false);
    }
  };

  const refineSyllabus = async (msgs: Message[], current: Syllabus) => {
    if (!current) return;
    try {
      const response = await supabase.functions.invoke("course-design-chat", {
        body: {
          messages: msgs,
          generateSyllabus: true,
          generateSyllabusPhase: "refine",
          currentSyllabus: current,
        },
      });
      if (response.error) throw new Error(response.error.message);
      if (response.data.syllabus) {
        setSyllabus(response.data.syllabus);
      }
    } catch (error) {
      console.error("Error refining syllabus:", error);
    }
  };

  const startConversation = async () => {
    const userMessage: Message = { role: "user", content: `I want to create a course about: ${initialTopic}` };
    setMessages([userMessage]);
    setIsLoading(true);

    try {
      const response = await supabase.functions.invoke("course-design-chat", {
        body: { messages: [userMessage] },
      });

      if (response.error) throw new Error(response.error.message);

      const assistantMessage: Message = { role: "assistant", content: response.data.message };
      const newMessages = [userMessage, assistantMessage];
      setMessages(newMessages);

      // Generate initial syllabus after first exchange
      await generateInitialSyllabus(newMessages);
    } catch (error) {
      console.error("Error starting conversation:", error);
      toast.error("Failed to start conversation. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = { role: "user", content: input };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);

    try {
      const response = await supabase.functions.invoke("course-design-chat", {
        body: { messages: newMessages },
      });

      if (response.error) throw new Error(response.error.message);

      const assistantMessage: Message = { role: "assistant", content: response.data.message };
      const updatedMessages = [...newMessages, assistantMessage];
      setMessages(updatedMessages);

      // Refine syllabus with new information
      if (syllabus) {
        refineSyllabus(updatedMessages, syllabus);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSubmit = async () => {
    if (!email.trim() || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    try {
      const { error } = await supabase.from("leads").insert({
        email: email.trim(),
        source: "course_design_chat",
        syllabus_topic: initialTopic,
      });

      if (error) {
        console.error("Error saving lead:", error);
        // Still unlock the syllabus even if saving fails
      }
      
      setEmailSubmitted(true);
      toast.success("Email submitted! You now have full access to the syllabus.");
    } catch (error) {
      console.error("Error saving lead:", error);
      setEmailSubmitted(true);
      toast.success("Email submitted! You now have full access to the syllabus.");
    }
  };

  const visibleSections = syllabus ? (emailSubmitted ? syllabus.sections : syllabus.sections.slice(0, 2)) : [];
  const hiddenSectionsCount = syllabus ? syllabus.sections.length - 2 : 0;

  const renderPlaceholder = (label: string) => (
    <span className="italic text-muted-foreground">
      To be specified — answer the question in the chat
    </span>
  );

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-5xl h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="font-semibold text-lg">Course Design Assistant</h2>
            <p className="text-sm text-muted-foreground">
              {syllabus ? "Your syllabus updates as you answer" : "Let's design your perfect course"}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Split: Chat (left) + Syllabus (right when available) */}
        <div className="flex-1 flex min-h-0">
          {/* Chat Panel */}
          <div className={`flex flex-col border-r ${syllabus ? "w-full md:w-[380px] flex-shrink-0" : "flex-1"}`}>
            <ScrollArea className="flex-1 p-4" ref={scrollRef}>
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[90%] rounded-lg px-4 py-2 ${
                        message.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      }`}
                    >
                      <p className="whitespace-pre-wrap text-sm">{message.content}</p>
                    </div>
                  </div>
                ))}
                {(isLoading || isGeneratingSyllabus) && (
                  <div className="flex justify-start">
                    <div className="bg-muted rounded-lg px-4 py-2 flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm text-muted-foreground">
                        {isGeneratingSyllabus ? "Generating syllabus..." : "Thinking..."}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            <div className="p-4 border-t">
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type your response..."
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                  disabled={isLoading || isGeneratingSyllabus}
                />
                <Button
                  onClick={sendMessage}
                  disabled={isLoading || isGeneratingSyllabus || !input.trim()}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Syllabus Panel - shown once syllabus exists */}
          {syllabus && (
            <ScrollArea className="flex-1 p-6" ref={syllabusScrollRef}>
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold mb-2">{syllabus.title}</h3>
                  <p className="text-muted-foreground">Your syllabus updates as you answer</p>
                </div>

                {/* Metadata fields - show blanks when placeholder */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="border rounded-lg p-4">
                    <h4 className="text-sm font-semibold mb-2">Timeline</h4>
                    {isPlaceholder(syllabus.timeline) ? (
                      renderPlaceholder("Timeline")
                    ) : (
                      <p className="text-muted-foreground">{syllabus.timeline}</p>
                    )}
                  </div>
                  <div className="border rounded-lg p-4">
                    <h4 className="text-sm font-semibold mb-2">Learner Profile</h4>
                    {isPlaceholder(syllabus.learnerProfile) ? (
                      renderPlaceholder("Learner Profile")
                    ) : (
                      <p className="text-muted-foreground">{syllabus.learnerProfile}</p>
                    )}
                  </div>
                  <div className="border rounded-lg p-4 sm:col-span-2">
                    <h4 className="text-sm font-semibold mb-2">Learning Objectives</h4>
                    {isPlaceholder(syllabus.learningObjectives) ? (
                      renderPlaceholder("Learning Objectives")
                    ) : (
                      <ul className="list-disc list-inside text-muted-foreground space-y-1">
                        {syllabus.learningObjectives?.map((obj, i) => (
                          <li key={i}>{obj}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="border rounded-lg p-4 sm:col-span-2">
                    <h4 className="text-sm font-semibold mb-2">Success Criteria</h4>
                    {isPlaceholder(syllabus.successCriteria) ? (
                      renderPlaceholder("Success Criteria")
                    ) : (
                      <p className="text-muted-foreground">{syllabus.successCriteria}</p>
                    )}
                  </div>
                </div>

                {/* Course sections */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-lg">Course Sections</h4>
                  {visibleSections.map((section, index) => (
                    <div key={index} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <h5 className="font-semibold">{section.title}</h5>
                        <span className="text-sm text-muted-foreground bg-muted px-2 py-1 rounded">
                          {section.duration}
                        </span>
                      </div>
                      <p className="text-muted-foreground text-sm">{section.description}</p>
                      <div>
                        <p className="text-sm font-medium mb-2">Topics covered:</p>
                        <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                          {section.topics.map((topic, i) => (
                            <li key={i}>{topic}</li>
                          ))}
                        </ul>
                      </div>
                      {section.references && section.references.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2">References:</p>
                          <ul className="text-sm space-y-1">
                            {section.references.map((ref, i) => (
                              <li key={i}>
                                <a
                                  href={ref.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:underline"
                                >
                                  {ref.title}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Email Gate */}
              {!emailSubmitted && hiddenSectionsCount > 0 && (
                <div className="border rounded-lg p-6 bg-muted/50 text-center space-y-4">
                  <div className="flex justify-center">
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Lock className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">
                      {hiddenSectionsCount} more sections available
                    </h4>
                    <p className="text-muted-foreground text-sm mt-1">
                      Enter your email to unlock the complete syllabus
                    </p>
                  </div>
                  <div className="flex gap-2 max-w-md mx-auto">
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="flex-1"
                    />
                    <Button onClick={handleEmailSubmit}>
                      <Mail className="h-4 w-4 mr-2" />
                      Unlock
                    </Button>
                  </div>
                </div>
              )}

                <div className="pt-4 border-t">
                  <Button onClick={onClose} variant="outline" className="w-full">
                    Close
                  </Button>
                </div>
              </div>
            </ScrollArea>
          )}
        </div>
      </Card>
    </div>
  );
}
